import {
    Component,
    Element,
    Event,
    EventEmitter,
    Listen,
    Prop,
    State,
    Watch,
    h,
} from '@stencil/core';
import { images } from '../../../helpers/inline-images.service';
import { getTranslatedSearchText } from '../language.util';
import { search } from './search.service';
import { SearchResult, Suggestion } from './suggestion.model';

@Component({
    tag: 'pnl-search',
    styleUrl: 'pnl-search.scss',
})
export class Search {
    handleSearchChange = (event) => {
        this.query = event.target.value;
        this.search();
    };

    @Element() component: HTMLElement;

    @State() query: string = '';
    @State() searchResult: SearchResult = null;
    @State() active = false;

    @Prop() domain: string;
    @Prop() locale: string;

    /** Emits an event when the searchbox active state changes. Event.detail will contains true if it is active, or false if not. */
    @Event() searchActivated: EventEmitter<boolean>;

    @Listen('focusin')
    handleFocusIn(event: FocusEvent) {
        if (event.relatedTarget instanceof Node && this.component.contains(event.relatedTarget)) {
            return;
        }

        this.active = true;
    }

    @Listen('focusout')
    handleFocusOut(event: FocusEvent) {
        if (event.relatedTarget instanceof Node && this.component.contains(event.relatedTarget)) {
            return;
        }

        this.active = false;
    }

    @Watch('active')
    handleActiveChanged() {
        this.searchActivated.emit(this.active);
    }

    // Note: debounce on handleSearchChange does not work when in shadow dom.
    // event.target is the pnl-header component instead of the input...
    // @Debounce()
    async search() {
        if (this.query.trim() === '') {
            this.searchResult = null;
            return;
        }

        this.searchResult = await search(this.query, this.locale, this.domain);
    }

    handleSubmit(e: Event) {
        e.preventDefault();
        const query = encodeURI(this.query.trim());
        if (query === '') return;

        const searchAction = `https://www.${this.domain}/search?q=` + query;
        window.location.href = searchAction;
    }

    renderAutoComplete(term: string) {
        return (
            <li class="pnl-search-form__suggestions-list-item">
                <a
                    class="pnl-search-form__suggestions-link"
                    href={`https://www.${this.domain}/search?q=${term}&searchEntry=auto-suggest`}
                >
                    {term}
                </a>
            </li>
        );
    }

    renderSuggestion(suggestion: Suggestion) {
        return (
            <li class="pnl-search-form__suggestions-list-item pnl-search-form__suggestions-list-item--direct">
                <a
                    class="pnl-search-form__suggestions-link"
                    href={suggestion.button.Url}
                    target="_blank"
                >
                    <div class="pnl-search-form__suggestions-image">
                        <img
                            alt=""
                            src={`${suggestion.image.Domain}${suggestion.image.Url}`}
                        />
                    </div>
                    <div>
                        <h2 class="pnl-search-form__suggestions-title">
                            {suggestion.title}
                        </h2>
                        <p class="pnl-search-form__suggestions-text">
                            {suggestion.description}
                        </p>
                    </div>
                </a>
            </li>
        );
    }

    render() {
        return (
            <div class="pnl-search-form pci-flyout">
                <form
                    id="search-form"
                    class="pnl-search-form__form"
                    onSubmit={(e) => this.handleSubmit(e)}
                >
                    <label
                        class="pnl-search-form__label visually-hidden"
                        htmlFor="pnl-search-input"
                    >
                        {getTranslatedSearchText(this.locale).label}
                    </label>
                    <input
                        id="pnl-search-input"
                        class="pnl-text-input__text pnl-search-form__input"
                        name="q"
                        placeholder={getTranslatedSearchText(this.locale).label}
                        autocomplete="off"
                        value={this.query}
                        onInput={this.handleSearchChange}
                    />
                    <button
                        type="submit"
                        class="pnl-search-form__button"
                    >
                        <div
                            class="pnl-search-form__icon"
                            innerHTML={images.search}
                        ></div>
                        <span class="visually-hidden">
                            {getTranslatedSearchText(this.locale).button}
                        </span>
                    </button>
                    {this.active && this.searchResult?.available && (
                        <div class="pnl-search-form__suggestions">
                            <ul class="pci-flyout__list pnl-search-form__suggestions-list">
                                {this.searchResult.autocomplete.map((ac) =>
                                    this.renderAutoComplete(ac)
                                )}
                                {this.searchResult.suggestions.map((sg) =>
                                    this.renderSuggestion(sg)
                                )}
                            </ul>
                        </div>
                    )}
                </form>
            </div>
        );
    }
}
